import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { User } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { RequirementsForm } from "@/components/RequirementsForm";
import { TopFrame } from "@/components/dashboard/TopFrame";
import { ProjectSelection } from "@/components/dashboard/ProjectSelection";
import { OrganizationStructureEditor } from "@/components/dashboard/OrganizationStructureEditor";
import { OrganizationalStructure } from "@/components/dashboard/OrganizationalStructure";
import { DefineRoles } from "@/components/dashboard/DefineRoles";
import { FindCandidates } from "@/components/dashboard/FindCandidates";
import { Summary } from "@/components/dashboard/Summary";
import { toast, Toaster } from "sonner";
import { ErrorAlert } from "@/components/dashboard/organization-editor/AlertDialogs";
import { supabase } from "@/integrations/supabase/client";
const organizationalStructures = [{
  value: "hierarchical",
  label: "Hierarchical Structure",
  description: "Traditional top-down management structure with clear reporting lines and chain of command. Each employee has one direct supervisor.",
  image: "hierarchical.png"
}, {
  value: "functional",
  label: "Functional Structure",
  description: "Organizes employees by their specific skills and department function (e.g., marketing, finance, HR). Promotes specialization and efficiency.",
  image: "functional.png"
}, {
  value: "divisional",
  label: "Divisional Structure",
  description: "Separates the organization into semi-autonomous divisions based on products, markets, or regions. Each division operates independently.",
  image: "divisional.png"
}, {
  value: "matrix",
  label: "Matrix Structure",
  description: "Employees report to both functional managers and project managers. Combines functional and divisional structures for increased flexibility.",
  image: "matrix.png"
}, {
  value: "flat",
  label: "Flat Structure",
  description: "Minimal management layers between staff and executives. Promotes direct communication and faster decision-making.",
  image: "flat.png"
}, {
  value: "team",
  label: "Team-Based Structure",
  description: "Organizes employees into teams based on projects or objectives. Emphasizes collaboration and cross-functional work.",
  image: "team.png"
}, {
  value: "network",
  label: "Network Structure",
  description: "Connects independent entities through contracts and shared goals. Ideal for organizations with multiple partners or contractors.",
  image: "network.png"
}, {
  value: "process",
  label: "Process-Based Structure",
  description: "Organizes workflow around core business processes rather than departments. Focuses on end-to-end value delivery.",
  image: "process.png"
}, {
  value: "circular",
  label: "Circular Structure",
  description: "Places executives in the center with teams radiating outward. Emphasizes equality and collaboration across departments.",
  image: "circular.png"
}, {
  value: "line",
  label: "Line Structure",
  description: "A clear chain of command where each person reports to one supervisor. Authority flows from top to bottom in a direct line.",
  image: "line.png"
}];
export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentStep = parseInt(searchParams.get('step') || '1');
  const urlProjectId = searchParams.get('projectId');
  const [user, setUser] = useState<User | null>(null);
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [selectedStructure, setSelectedStructure] = useState<string>("hierarchical");
  const [isLoading, setIsLoading] = useState(true);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showRequirementsSaveDialog, setShowRequirementsSaveDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isProjectSaved, setIsProjectSaved] = useState(false);
  const [isSelectedProjectSaved, setIsSelectedProjectSaved] = useState(false);
  const [moduleSelected, setModuleSelected] = useState<boolean>(false);
  useEffect(() => {
    const checkSession = async () => {
      try {
        const {
          data: {
            session
          },
          error: sessionError
        } = await supabase.auth.getSession();
        if (sessionError) {
          console.error('Session error:', sessionError);
          throw sessionError;
        }
        if (!session) {
          toast.error("Please sign in to access the dashboard");
          navigate('/');
          return;
        }

        // Check if user accessed this page directly without selecting the HR module
        const selectedModule = localStorage.getItem('selectedModule');
        if (!selectedModule || selectedModule !== 'talent-acquisition') {
          toast.error("Please select the HR module from the homepage");
          navigate('/');
          return;
        }
        setModuleSelected(true);
        setUser(session.user);
      } catch (error) {
        console.error('Authentication error:', error);
        toast.error("Session expired. Please sign in again.");
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
        navigate('/');
        return;
      }
      if (session?.user) {
        // Even if auth state changes, still check if module was properly selected
        const selectedModule = localStorage.getItem('selectedModule');
        if (!selectedModule || selectedModule !== 'talent-acquisition') {
          navigate('/');
          return;
        }
        setUser(session.user);
      }
    });
    checkSession();
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);
  useEffect(() => {
    const findAndSetProject = async () => {
      if (!urlProjectId) return;
      try {
        if (currentStep === 1) {
          const {
            data: orgStructure,
            error: orgError
          } = await supabase.from('organization_structures').select('project_id').eq('project_id', urlProjectId).maybeSingle();
          if (orgError) {
            console.error('Error fetching organization structure:', orgError);
            return;
          }
          if (orgStructure?.project_id) {
            setSelectedProject(orgStructure.project_id);
          }
        } else {
          setSelectedProject(urlProjectId);
        }
      } catch (error) {
        console.error('Error in findAndSetProject:', error);
      }
    };
    findAndSetProject();
  }, [urlProjectId, currentStep]);
  useEffect(() => {
    const checkProjectStatus = async () => {
      if (!selectedProject) {
        setIsProjectSaved(false);
        return;
      }
      try {
        const {
          data,
          error
        } = await supabase.from('project_requirements').select('is_saved').eq('id', selectedProject).maybeSingle();
        if (error) {
          console.error('Error checking project status:', error);
          return;
        }
        setIsProjectSaved(data?.is_saved || false);
      } catch (error) {
        console.error('Error in checkProjectStatus:', error);
      }
    };
    checkProjectStatus();
  }, [selectedProject]);
  const handleProjectSelect = (projectId: string | null, isSaved: boolean) => {
    setSelectedProject(projectId);
    setIsSelectedProjectSaved(isSaved);
    if (projectId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('projectId', projectId);
      navigate(`/dashboard?${newParams.toString()}`);
    }
  };
  const handleNextStep = async () => {
    if (!selectedProject) {
      toast.error("Please select a project first");
      return;
    }
    if (!isSelectedProjectSaved) {
      toast.error("Please save the project requirements first");
      return;
    }
    try {
      console.log('Checking project:', selectedProject);

      // First check if the project exists and get its requirements
      const {
        data: requirements,
        error: reqError
      } = await supabase.from('project_requirements').select('is_saved, id').eq('id', selectedProject).maybeSingle();
      if (reqError) {
        console.error('Error checking requirements:', reqError);
        toast.error("Failed to check project status");
        return;
      }
      if (!requirements) {
        console.error('No requirements found for project:', selectedProject);
        toast.error("Project requirements not found");
        return;
      }
      if (!requirements.is_saved) {
        toast.error("Please save the project requirements first");
        return;
      }
      navigate(`/dashboard?step=2&projectId=${requirements.id}`);
      toast.success("Moving to Organization Structure Editor");
    } catch (error) {
      console.error('Error in handleNextStep:', error);
      toast.error("An unexpected error occurred");
    }
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <>
            <div className="grid grid-cols-12 gap-6 mt-2 h-[515px] py-[17px]">
              <div className="col-span-4 h-[475px]">
                <ProjectSelection onProjectSelect={handleProjectSelect} selectedProjectId={selectedProject} />
              </div>
              <div className="col-span-4 h-[475px]">
                <RequirementsForm projectId={selectedProject} selectedStructure={selectedStructure} setSelectedStructure={setSelectedStructure} organizationalStructures={organizationalStructures} onSave={() => {
                toast.success("Requirements saved successfully");
                setIsProjectSaved(true);
                setIsSelectedProjectSaved(true);
              }} onNext={projectId => {
                if (projectId) {
                  navigate(`/dashboard?step=2&projectId=${projectId}`);
                  toast.success("Moving to Organization Structure Editor");
                }
              }} />
              </div>
              <div className="col-span-4 h-[475px] text-primary-light">
                <OrganizationalStructure selectedStructure={selectedStructure} organizationalStructures={organizationalStructures} />
              </div>
            </div>
            <Separator className="my-2 bg-primary/20" />
            <div className="flex justify-center items-center gap-4 my-[15px]">
              <Button type="button" onClick={() => document.querySelector('form')?.requestSubmit()} className="w-32 bg-white text-secondary hover:bg-white/90" disabled={isProjectSaved}>
                Save
              </Button>
              <Button onClick={handleNextStep} className="w-32" disabled={!isSelectedProjectSaved}>
                Next
              </Button>
            </div>
          </>;
      case 2:
        return <OrganizationStructureEditor />;
      case 3:
        return <DefineRoles />;
      case 4:
        return <FindCandidates />;
      case 5:
        return <Summary />;
      default:
        return null;
    }
  };
  if (isLoading) {
    return null;
  }
  if (!user || !moduleSelected) {
    return null;
  }
  return <div className="min-h-screen bg-secondary">
      <TopFrame user={user} />
      <main className="max-w-[1800px] mx-auto px-4 h-[510px]">
        <div className="mt-0 h-[540px]">
          {renderStepContent()}
        </div>
      </main>

      <ErrorAlert open={showErrorDialog} onOpenChange={setShowErrorDialog} title="Error" description="Failed to check requirements status" />

      <ErrorAlert open={showRequirementsSaveDialog} onOpenChange={setShowRequirementsSaveDialog} title="Save Required" description="Project requirements has to be saved first" />

      <ErrorAlert open={showValidationDialog} onOpenChange={setShowValidationDialog} title="Save Required" description="The number of Directors, Managers, and Team Members cannot be zero" />
    </div>;
}